import { useEffect, useState } from 'react';
import { FormControlLabel, Radio, RadioGroup, Stack, TextField, Typography } from '@mui/material';
import { Account, PermissionSet, PermissionSetDomain, PermissionSetRules } from '../../../types';
import { createPermissionSet, updatePermissionSet } from '../../../store/PermissionSets';
import { getAccountByIdOrSfId } from '../../../store/Accounts';
import { Tag, fetchTags, getTags } from '../../../store/Tags';
import { useSnackbar } from '../../../Context/SnackbarContext';
import { DEFAULT_ADVANCE_BUTTON_STYLE, DEFAULT_CANCEL_BUTTON_STYLE } from '../../../utils/styleHelpers';
import { AccountSelectionForm } from '../UserCreation';
import { deriveAccessLevel } from './derivations';
import { DomainMatrix } from './DomainMatrix';
import { FieldRestrictionsPicker } from './FieldRestrictionsPicker';
import { TargetedDeniesEditor } from './TargetedDeniesEditor';
import { extractApiErrorMessage } from '../McpAccess/extractApiErrorMessage';

export interface PermissionSetEditorProps {
  mode: 'create' | 'edit';
  permissionSet?: PermissionSet | null;
  onCancel: () => void;
  onSaved: () => void;
}

type AccessLevel = 'read_only' | 'full_access';

const initialAccessLevel = (rules?: PermissionSetRules): AccessLevel =>
  rules && deriveAccessLevel(rules) === 'Full Access' ? 'full_access' : 'read_only';

const applyWriteToAll = (rules: PermissionSetRules, write: boolean): PermissionSetRules =>
  Object.fromEntries(
    (Object.entries(rules) as [PermissionSetDomain, PermissionSetRules[PermissionSetDomain]][]).map(([key, rule]) => [
      key,
      { ...rule, write }
    ])
  );

const SAVE_FAILED = 'Could not save permission set';

export const PermissionSetEditor = ({ mode, permissionSet, onCancel, onSaved }: PermissionSetEditorProps) => {
  const { setSnack } = useSnackbar();
  const [name, setName] = useState(permissionSet?.name ?? '');
  const [description, setDescription] = useState<string>(permissionSet?.description ?? '');
  const [rules, setRules] = useState<PermissionSetRules>(permissionSet?.rules ?? {});
  const [denies, setDenies] = useState<string[]>(permissionSet?.targeted_denies ?? []);
  const [accessLevel, setAccessLevel] = useState<AccessLevel>(initialAccessLevel(permissionSet?.rules));
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const [tagOptions, setTagOptions] = useState<Tag[]>([]);
  const [fieldPickerDomain, setFieldPickerDomain] = useState<PermissionSetDomain | null>(null);
  const [saving, setSaving] = useState(false);
  const accountId = selectedAccount?.id ?? '';

  useEffect(() => {
    const boundAccountId = permissionSet?.bound_account_ids?.[0];
    if (!boundAccountId) return;
    let active = true;
    getAccountByIdOrSfId(boundAccountId).then((account) => {
      if (active && account) setSelectedAccount(account);
    });
    return () => {
      active = false;
    };
  }, [permissionSet]);

  useEffect(() => {
    if (!accountId) {
      setTagOptions([]);
      return;
    }
    let active = true;
    fetchTags(false, accountId).then(() => active && setTagOptions(getTags()));
    return () => {
      active = false;
    };
  }, [accountId]);

  const handleAccessLevel = (level: AccessLevel) => {
    setAccessLevel(level);
    setRules((current) => applyWriteToAll(current, level === 'full_access'));
  };

  const handleSave = async () => {
    setSaving(true);
    const body = { name: name.trim(), description: description.trim() || null, rules, targeted_denies: denies };
    const result =
      mode === 'edit' && permissionSet
        ? await updatePermissionSet(permissionSet.id, body)
        : await createPermissionSet(body);
    setSaving(false);
    if (result.error) {
      setSnack({ message: extractApiErrorMessage(result.error, SAVE_FAILED), type: 'error', open: true });
      return;
    }
    setSnack({ message: `Permission set ${mode === 'edit' ? 'updated' : 'created'}`, type: 'success', open: true });
    onSaved();
  };

  return (
    <Stack
      spacing={3}
      data-cy="permission-set-editor">
      <Typography variant="h6">{mode === 'edit' ? 'Edit Permission Set' : 'Create Permission Set'}</Typography>

      <Stack spacing={2}>
        <Typography variant="subtitle2">Basic Information</Typography>
        <TextField
          label="Permission Set Name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          required
          fullWidth
        />
        <TextField
          label="Description"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          fullWidth
        />
        <RadioGroup
          row
          value={accessLevel}
          onChange={(event) => handleAccessLevel(event.target.value as AccessLevel)}>
          <FormControlLabel
            value="read_only"
            control={<Radio />}
            label="Read Only"
          />
          <FormControlLabel
            value="full_access"
            control={<Radio />}
            label="Full Access"
          />
        </RadioGroup>
      </Stack>

      <Stack spacing={0.5}>
        <Typography variant="subtitle2">Field &amp; tag source account</Typography>
        <Typography
          variant="caption"
          color="text.secondary">
          Used to list this account&apos;s fields and tags while authoring restrictions.
        </Typography>
        <AccountSelectionForm
          selectedAccount={selectedAccount}
          setSelectedAccount={(account) => setSelectedAccount(account ?? null)}
          showLabel={false}
          styles="w-96"
        />
      </Stack>

      <Stack spacing={1}>
        <Typography variant="subtitle2">Feature Permissions</Typography>
        <DomainMatrix
          rules={rules}
          tagOptions={tagOptions}
          defaultWrite={accessLevel === 'full_access'}
          onChange={setRules}
          onEditFields={setFieldPickerDomain}
        />
      </Stack>

      <Stack spacing={1}>
        <Typography variant="subtitle2">Action Restrictions</Typography>
        <TargetedDeniesEditor
          value={denies}
          onChange={setDenies}
        />
      </Stack>

      {fieldPickerDomain && (
        <FieldRestrictionsPicker
          open
          domain={fieldPickerDomain}
          accountId={accountId}
          hiddenFields={rules[fieldPickerDomain]?.hide_fields ?? []}
          onChange={(hidden) =>
            setRules((current) => ({
              ...current,
              [fieldPickerDomain]: { ...current[fieldPickerDomain], hide_fields: hidden }
            }))
          }
          onClose={() => setFieldPickerDomain(null)}
        />
      )}

      <Stack
        direction="row"
        spacing={2}
        justifyContent="flex-end">
        <button
          type="button"
          className={DEFAULT_CANCEL_BUTTON_STYLE}
          onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          className={DEFAULT_ADVANCE_BUTTON_STYLE}
          disabled={!name.trim() || saving}
          onClick={handleSave}>
          Save Permission Set
        </button>
      </Stack>
    </Stack>
  );
};
